import {
  calculateInventoryBreakdown,
  StoreProduct,
  StoreProductFraction,
  hasStockForUnits,
} from '@bofrak-backend/shared';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Text,
  TextProps,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { getInventoryDisplayParts } from '../../utils';
import { useSharedUiTranslation } from '../../i18n';

export interface InventoryBreakdownProps extends Omit<TextProps, 'children'> {
  /** Total consolidated inventory value */
  inventory: number;
  /**
   * Parent product unit name (for display).
   * If not provided, defaults to first item in fractions with fraction = 1
   */
  parentUnit?: string | number;
  /**
   * Array of all fractions (including parent).
   * Should contain objects with unit name and fraction value.
   * Parent typically has fraction = 1, children have fraction < 1
   */
  fractions?: Array<StoreProduct | StoreProductFraction>;
  /** Whether to show only the consolidated value (no breakdown) */
  consolidatedOnly?: boolean;
  /** Placeholder when inventory is not provided */
  placeholder?: React.ReactNode;
  /** Label for the popover */
  label?: string;
  /** Whether the inventory display should act as a button (clickable) */
  asButton?: boolean;
  /** Low inventory threshold for button color coding */
  lowInventoryThreshold?: number;
  /** Smallest fraction for color coding */
  smallestFraction?: number;
  /** Whether to show "IN STOCK" text for positive inventory */
  showInStockText?: boolean;
}

/**
 * Displays inventory with a breakdown by parent/child fractions.
 * Shows consolidated value with a popover containing the breakdown.
 *
 * Example breakdown for inventory = 5.7:
 * - Parent A (fraction 1): 5 units
 * - Child A1 (fraction 0.5): 1 unit
 * - Child A2 (fraction 0.1): 2 units
 */
export const InventoryBreakdown: React.FC<InventoryBreakdownProps> = ({
  inventory,
  parentUnit,
  fractions = [],
  consolidatedOnly = false,
  asButton = false,
  lowInventoryThreshold,
  smallestFraction,
  showInStockText = true,
  placeholder = '-',
  label,
  ...textProps
}) => {
  const { t } = useSharedUiTranslation();
  const { display, isValueProvided } = getInventoryDisplayParts(inventory);

  // Calculate breakdown
  const breakdown = useMemo(() => {
    if (consolidatedOnly || !isValueProvided || inventory <= 0) {
      return [];
    }
    return calculateInventoryBreakdown(inventory, fractions, parentUnit);
  }, [inventory, fractions, consolidatedOnly, isValueProvided]);

  const hasStock = smallestFraction
    ? hasStockForUnits(inventory, smallestFraction)
    : inventory > 0;
  const color = hasStock
    ? inventory < (lowInventoryThreshold ?? 0)
      ? 'yellow.600'
      : 'green.600'
    : 'red.600';

  // If no value provided, show placeholder
  if (!isValueProvided) {
    return (
      <Text as="span" {...textProps}>
        {placeholder}
      </Text>
    );
  }

  // If consolidated only or no breakdown available, show simple value
  if (consolidatedOnly || breakdown.length === 0) {
    return (
      <Text color={color} as="span" {...textProps}>
        {display}
      </Text>
    );
  }

  return (
    <Popover trigger="click" placement="top">
      <PopoverTrigger>
        {asButton ? (
          <Button
            color={color}
            border={'none'}
            width={'fit-content'}
            size="x-small"
            fontSize={'x-small'}
            variant="outline">
            {display} {showInStockText ? t('IN STOCK') : ''}
          </Button>
        ) : (
          <HStack
            as="button"
            display="inline-flex"
            alignItems="center"
            spacing={1}
            cursor="pointer"
            _hover={{ color: 'blue.500' }}>
            <Text color={color} as="span" {...textProps} fontSize={'xs'}>
              {display} {showInStockText ? t('IN STOCK') : ''}
            </Text>
            <InfoOutlineIcon boxSize={3} color="currentColor" />
          </HStack>
        )}
      </PopoverTrigger>
      <PopoverContent width="auto" minW="180px" maxW="280px">
        <PopoverArrow />
        <PopoverBody p={3}>
          <VStack align="stretch" spacing={2}>
            {/* Consolidated Total */}
            <Box borderBottom="1px solid" borderColor="gray.200" pb={2}>
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                {label ?? t('Inventory Breakdown')}
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {t('Total: {{value}}', { value: display })}
              </Text>
            </Box>

            {/* Breakdown by fraction */}
            <VStack align="stretch" spacing={1}>
              {breakdown.map((item, index) => (
                <HStack key={index} justify="space-between" fontSize="sm">
                  <Text color="gray.600" noOfLines={1} maxW="150px">
                    {item.name}
                  </Text>
                  <Spacer />
                  <Text fontWeight="medium">{item.units}</Text>
                </HStack>
              ))}
            </VStack>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
